// Shop signs and plaques.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add } from '../lib/factory.js';
import { instanced } from '../lib/instancing.js';
import { textTex } from '../textures/signs.js';
import { SHOPS, colliders, containers, parked, plaques, shops } from './eixample.js';
import { streets } from './landmarks.js';

export let signMats;

export function buildSigns() {
  signMats = SHOPS.map(([name, bg]) =>
    M(0xffffff, {
      flatShading: false,
      emissive: 0xffffff,
      emissiveIntensity: 0,
      map: textTex(256, 96, (c) => {
        c.fillStyle = bg;
        c.fillRect(0, 0, 256, 96);
        c.strokeStyle = 'rgba(255,255,255,.7)';
        c.lineWidth = 4;
        c.strokeRect(6, 6, 244, 84);
        c.fillStyle = '#fff';
        c.font = 'bold 34px Georgia, serif';
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(name, 128, 50, 230);
      }),
    }),
  );
  signMats.forEach((m) => (m.emissiveMap = m.map));
  const signEdge = M(0x2b2b2b),
    pharmacyMat = M(0x2fd36a, { emissive: 0x2fd36a, emissiveIntensity: 1.4, flatShading: false });
  for (const sh of shops)
    if (sh.sign >= 0) {
      const tx = Math.cos(sh.ry),
        tz = -Math.sin(sh.ry),
        nx = Math.sin(sh.ry),
        nz = Math.cos(sh.ry),
        m = signMats[sh.sign];
      if (sh.sign === 0) {
        const cross = new THREE.Group();
        cross.position.set(sh.x + tx * 0.65 + nx * 0.32, 1.62, sh.z + tz * 0.65 + nz * 0.32);
        cross.rotation.y = sh.ry;
        scene.add(cross);
        const gm = pharmacyMat;
        add(new RoundedBoxGeometry(0.05, 0.4, 0.13, 2, 0.02), gm, 0, 0, 0, cross);
        add(new RoundedBoxGeometry(0.05, 0.13, 0.4, 2, 0.02), gm, 0, 0, 0, cross);
        add(new THREE.BoxGeometry(0.03, 0.03, 0.2), signEdge, 0, 0, -0.25, cross);
      } else {
        const b = add(
          new THREE.BoxGeometry(0.04, 0.26, 0.5),
          [m, m, signEdge, signEdge, signEdge, signEdge],
          sh.x + tx * 0.65 + nx * 0.4,
          1.62,
          sh.z + tz * 0.65 + nz * 0.4,
        );
        b.rotation.y = sh.ry;
        add(
          new THREE.BoxGeometry(0.02, 0.02, 0.2),
          signEdge,
          sh.x + tx * 0.65 + nx * 0.06,
          1.75,
          sh.z + tz * 0.65 + nz * 0.06,
        ).rotation.y = sh.ry;
      }
    }
  const plaqueMats = {};
  for (const pq of plaques) {
    plaqueMats[pq.name] ??= M(0xffffff, {
      flatShading: false,
      roughness: 0.4,
      map: textTex(256, 128, (c) => {
        c.fillStyle = '#f4f1ea';
        c.fillRect(0, 0, 256, 128);
        c.strokeStyle = '#2a2a2a';
        c.lineWidth = 5;
        c.strokeRect(8, 8, 240, 112);
        c.fillStyle = '#222';
        c.textAlign = 'center';
        c.font = '24px Georgia, serif';
        c.fillText('Districte de l’Eixample', 128, 36);
        const words = pq.name.split(' ');
        c.font = 'bold 30px Georgia, serif';
        c.fillText(words.slice(0, -1).join(' '), 128, 72, 230);
        c.fillText(words.at(-1), 128, 104, 230);
      }),
    });
    const pm = plaqueMats[pq.name],
      edge = M(0xd9d4c8);
    add(
      new THREE.BoxGeometry(0.52, 0.27, 0.025),
      [edge, edge, edge, edge, pm, edge],
      pq.x,
      1.75,
      pq.z,
    ).rotation.y = pq.ry;
  }
  {
    for (const p of parked) colliders.push({ type: 'ell', cx: p.x, cz: p.z, a: 0.3, b: 0.3 });
    const holes = [];
    for (const c of streets)
      for (let t = -42; t <= 42; t += rr(9, 15)) {
        if (streets.some((k) => Math.abs(t - k) < 8)) continue;
        holes.push(rnd() < 0.5 ? { x: c + rr(-0.9, 0.9), z: t } : { x: t, z: c + rr(-0.9, 0.9) });
      }
    const mhTex = textTex(64, 64, (c) => {
      c.fillStyle = '#3d3b38';
      c.fillRect(0, 0, 64, 64);
      c.strokeStyle = '#5a5752';
      c.lineWidth = 2;
      for (let r = 8; r < 32; r += 6) {
        c.beginPath();
        c.arc(32, 32, r, 0, PI * 2);
        c.stroke();
      }
      for (let a = 0; a < 8; a++) {
        c.beginPath();
        c.moveTo(32, 32);
        c.lineTo(32 + Math.cos((a * PI) / 4) * 30, 32 + Math.sin((a * PI) / 4) * 30);
        c.stroke();
      }
    });
    instanced(
      new THREE.CylinderGeometry(0.36, 0.36, 0.02, 24),
      P(0xffffff, { map: mhTex, roughness: 0.5, metalness: 0.6 }),
      holes,
      (o, h) => o.position.set(h.x, 0.01, h.z),
    ).castShadow = false;
    const bol = [];
    for (const ix of streets)
      for (const iz of streets)
        for (const s of [-1, 1])
          for (const o of [-1, 1]) {
            bol.push({ x: ix + o * 2.5, z: iz + s * 7.4 });
            bol.push({ x: ix + s * 7.4, z: iz + o * 2.5 });
          }
    instanced(
      mergeGeometries([
        new THREE.CylinderGeometry(0.06, 0.07, 0.5, 12).translate(0, 0.25, 0),
        new THREE.SphereGeometry(0.065, 12, 8).translate(0, 0.5, 0),
      ]),
      P(0x2b2f2c, { roughness: 0.4, metalness: 0.5 }),
      bol,
      (o, b) => o.position.set(b.x, 0.3, b.z),
    );
  }
  instanced(
    mergeGeometries([
      new THREE.BoxGeometry(0.85, 0.95, 0.75).translate(0, 0.475, 0),
      new THREE.BoxGeometry(0.9, 0.08, 0.8).translate(0, 1, 0),
    ]),
    M(0xffffff, { roughness: 0.6 }),
    containers,
    (o, c) => {
      o.position.set(c.x, 0.3, c.z);
      o.rotation.y = c.ry;
    },
    [0x6b7075, 0xe8c02c, 0x2f6fb5, 0x3f8f4f, 0x7a5a3a],
  );
}
